import React, { Fragment, useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, Text } from 'rebass/styled-components';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import invariant from 'tiny-invariant';
import { trackEvent } from 'helpers/analytics';
import { toggleMenu } from '../../../features/mainMenu';
import { activeNetworkSelector } from '../../../redux/selectors';
import { useMainMenu } from '../../../hooks/useMainMenu';
import { MenuNav } from './menuNav';
import JobLinks from './jobLinks';
import ListLinks from './listLinks';
import { NavHeader } from './navHeader';
import { NetworkNav } from './networkNav';

export const MainLayoutMenu = ({ width, setMenuWidth, contentRef }) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const isMenuOpen = useSelector((state) => state.mainMenu.isOpen);
  const [initialWidth, setInitialWidth] = useState();
  const { menuItems, sourcingAgentItems } = useMainMenu(activeNetwork);
  const dispatch = useDispatch();

  const sidebarRef = useRef(null);
  const dividerRef = useRef(null);
  const [state, setState] = useState({
    type: 'idle',
  });

  const widths = {
    start: 200,
    min: 200,
    max: 440,
  };

  const getProposedWidth = useCallback(
    ({ initialWidth: startWidth, location }) => {
      const diffX = location.current.input.clientX - location.initial.input.clientX;
      const proposedWidth = startWidth + diffX;

      // ensure we don't go below the min or above the max allowed widths
      return Math.min(Math.max(widths.min, proposedWidth), widths.max);
    },
    [widths.min, widths.max],
  );

  useEffect(() => {
    const divider = dividerRef.current;
    invariant(divider);

    const cleanup = draggable({
      element: divider,
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        // we will be moving the line to indicate a drag
        // we can disable the native drag preview
        disableNativeDragPreview({ nativeSetDragImage });
        // we don't want any native drop animation for when the user
        // does not drop on a drop target. we want the drag to finish immediately
        preventUnhandled.start();
      },
      onDragStart() {
        setInitialWidth(width);
        setState({ type: 'dragging' });
        contentRef.current?.style.setProperty('transition', 'none');
      },
      onDrag({ location }) {
        const newWidth = getProposedWidth({ initialWidth, location });
        contentRef.current?.style.setProperty('margin-left', `${newWidth + 49}px`);
        sidebarRef.current?.style.setProperty('width', `${newWidth}px`);
      },
      onDrop({ location }) {
        preventUnhandled.stop();
        setState({ type: 'idle' });
        const newWidth = getProposedWidth({ initialWidth, location });
        setMenuWidth(newWidth);
        localStorage.setItem('sidebar-width', newWidth.toString());
        contentRef.current?.style.removeProperty('margin-left');
        contentRef.current?.style.removeProperty('transition');
        sidebarRef.current?.style.removeProperty('width');
      },
    });

    return cleanup;
  }, [width, setMenuWidth, getProposedWidth, contentRef, initialWidth]);

  return (
    <>
      {/* Overlay */}
      <Box
        display={[isMenuOpen ? 'block' : 'none', isMenuOpen ? 'block' : 'none', 'none']}
        bg="neutral.20"
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 98,
          opacity: 0.8,
        }}
        onClick={() => dispatch(toggleMenu())}
      />

      {/* Sidebar */}
      <Flex
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: [99, 99, 15],
          boxShadow: [isMenuOpen ? 'medium' : 'none', 'none', 'none'],
          transition: 'transform 0.15s ease-in-out',
          transform: isMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
        }}
      >
        <NetworkNav />
        <Flex
          ref={sidebarRef}
          className="main-menu"
          height="100vh"
          width={['calc(100vw - 80px)', 'calc(100vw - 80px)', width]}
          maxWidth="440px"
          flexDirection="column"
          bg={['neutral.0', 'neutral.0', 'neutral.20']}
          pb="8px"
          sx={{
            borderRight: 1,
            borderRightColor: 'neutral.100',
            overflow: ['visible', 'visible', 'hidden'],
          }}
        >
          <Box
            ref={dividerRef}
            sx={{
              display: ['none', 'none', 'block'],
              position: 'absolute',
              right: 0,
              top: 0,
              width: '4px',
              height: '100%',
              borderRight: '2px solid',
              borderRightColor: state.type === 'dragging' ? 'purple.400' : 'transparent',
              cursor: 'col-resize',
              transition: 'border-right-color 0.15s ease-in-out',
              '&:hover': {
                borderRightColor: 'purple.400',
              },
            }}
          />
          <NavHeader activeNetwork={activeNetwork} />
          <Flex flexDirection="column" flexGrow="1" sx={{ minHeight: 0 }}>
            {!!menuItems.length && (
              <Box as="nav" mx="4px" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 auto' }}>
                <Box
                  as="ul"
                  sx={{
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    minHeight: 0,
                    gap: '24px',
                  }}
                >
                  <Box>
                    {menuItems.map(({ icon, label, path, href, items = [], groupName, type }) => (
                      <Fragment key={groupName || `${path}-${href}`}>
                        {items.length > 0 && groupName === 'Job board' && (
                          <JobLinks items={items} slug={activeNetwork.slug} groupName={groupName} />
                        )}
                        {items.length === 0 && (
                          <MenuNav
                            key={`${path}-${href}`}
                            path={path}
                            href={href}
                            label={label}
                            icon={icon}
                            type={type}
                            slug={activeNetwork.slug}
                          />
                        )}
                      </Fragment>
                    ))}
                  </Box>
                  {sourcingAgentItems.length > 0 && (
                    <Box>
                      <Box px="8px" my="4px" sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <Text
                          sx={{
                            color: 'text.subtle',
                            fontSize: '12px',
                            fontStyle: 'normal',
                            fontWeight: '500',
                            lineHeight: '16px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                          }}
                        >
                          AI Agents
                        </Text>
                        <Text
                          sx={{
                            background: 'linear-gradient(94deg, #4900FF 3.21%, #D400FF 63.82%, #FF9257 134.26%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textAlign: 'center',
                            fontSize: '10px',
                            fontStyle: 'italic',
                            fontWeight: '600',
                            lineHeight: '16px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            mb: '4px',
                          }}
                        >
                          BETA
                        </Text>
                      </Box>
                      {sourcingAgentItems.map(({ icon, label, path, href, type }) => (
                        <MenuNav
                          key={`${path}-${href}`}
                          path={path}
                          href={href}
                          label={label}
                          icon={icon}
                          slug={activeNetwork.slug}
                          type={type}
                          onClick={() => {
                            trackEvent('ai_agents:early_access_click', { collection_id: activeNetwork.id });
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  {activeNetwork.connect?.hasAccess && <ListLinks slug={activeNetwork.slug} />}
                </Box>
              </Box>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

MainLayoutMenu.propTypes = {
  width: PropTypes.number.isRequired,
  setMenuWidth: PropTypes.func.isRequired,
  contentRef: PropTypes.object,
};

MainLayoutMenu.defaultProps = {
  contentRef: null,
};
